import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
export function ServicesPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    title: 'Roofing & Repairs',
    slug: 'roofing',
    image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=600&q=80',
    description: 'Complete roof replacements, tear-offs, and emergency repairs using premium architectural shingles designed for Michigan weather.'
  }, {
    title: 'Gutter Services',
    slug: 'gutters',
    image: 'https://images.unsplash.com/photo-1585129777188-94600bc7b4b3?w=600&q=80',
    description: 'Custom seamless aluminum gutters, professional cleaning, and advanced gutter protection systems to keep debris out.'
  }, {
    title: 'Siding Installation',
    slug: 'siding',
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=80',
    description: "Transform your home's exterior with durable vinyl, fiber cement, or wood siding options that enhance curb appeal and insulation."
  }, {
    title: 'Window Replacement',
    slug: 'windows',
    image: 'https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?w=600&q=80',
    description: "Energy-efficient window upgrades that lower utility bills, reduce drafts, and beautifully update your home's appearance."
  }, {
    title: 'Exterior Painting',
    slug: 'painting',
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&q=80',
    description: 'Professional exterior painting services to protect your siding and trim while giving your home a fresh, updated look.'
  }, {
    title: 'Shingle Selection',
    slug: 'shingles',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&q=80',
    description: 'Browse our extensive catalog of premium architectural shingles from top brands to find the perfect color and style for your roof.'
  }];
  return <div className="w-full bg-white" data-id="element-956">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-957">
        <div className="absolute inset-0 z-0" data-id="element-958">
          <img src="https://images.unsplash.com/photo-1449844908441-8829872d2607?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-959" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-960"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 z-[2]" data-id="element-961"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-962">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-963">
            {/* Breadcrumbs */}
            <nav className="flex items-center text-sm text-gray-300 font-body mb-6" data-id="element-964">
              <Link to="/" className="hover:text-brand-blue transition-colors" data-id="element-965">
                Home
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-966" />
              <span className="text-white font-semibold" data-id="element-967">Services</span>
            </nav>

            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 drop-shadow-lg" data-id="element-968">
              Our Residential{' '}
              <span className="text-brand-blue" data-id="element-969">Exterior Services</span>
            </h1>
            <p className="font-body text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed drop-shadow-md" data-id="element-970">
              Comprehensive exterior solutions tailored to protect and beautify
              your home against Michigan's unique climate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50" data-id="element-971">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-972">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-973">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group flex flex-col h-full" data-id="element-974">
                <Link to={`/services/${service.slug}`} className="block relative aspect-[4/3] overflow-hidden" data-id="element-975">
                  <div className="brand-overlay h-full" data-id="element-976">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-977" />
                  </div>
                </Link>
                <div className="p-8 flex flex-col flex-grow" data-id="element-978">
                  <h3 className="font-heading font-bold text-2xl text-brand-gray-dark mb-4 group-hover:text-brand-blue transition-colors" data-id="element-979">
                    <Link to={`/services/${service.slug}`} data-id="element-980">
                      {service.title}
                    </Link>
                  </h3>
                  <p className="font-body text-brand-gray mb-8 flex-grow leading-relaxed" data-id="element-981">
                    {service.description}
                  </p>
                  <Link to={`/services/${service.slug}`} className="inline-flex items-center gap-2 text-brand-blue font-body font-bold hover:text-brand-blue-dark transition-colors mt-auto" data-id="element-982">
                    Learn More{' '}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-983" />
                  </Link>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA data-id="element-984" />
    </div>;
}